import React, { Fragment, useEffect, useRef, useState } from "react";
import { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";

import getStrain from "api/requests/getStrain";
import getStrainPhotos from "api/requests/getStrainPhotos";
import { ads } from "constants/ads";
import { Photo, Strain } from "custom-types/Strain";
import getStrainRedirect from "utils/getStrainRedirect";

import Button from "components/botanic/Button";
import PhotoGallery from "components/botanic/PhotoGallery";
import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import GoogleAd from "components/GoogleAd";
import MetaTags from "components/MetaTags";
import PageNavigation from "components/PageNavigation";
import Placeholder, { Rect } from "components/Placeholder";

const EAGER_LOAD_COUNT = 3;

type Props = {
  strain: Strain;
  initialPhotos: Photo[];
};

const Photos: NextPage<Props> = ({ strain, initialPhotos }) => {
  const router = useRouter();
  const {
    query: { page },
  } = router;
  const currentPage = page ? Number(page) : 1;

  const [loading, setLoading] = useState<boolean>(!initialPhotos.length);
  const [photos, setPhotos] = useState<Photo[]>(initialPhotos);
  const [totalPhotos, setTotalPhotos] = useState<number>(0);

  const ogImageUrl =
    photos[0]?.imageUrl &&
    `${photos[0].imageUrl}?auto=compress&w=1200&h=630&fit=crop`;

  // Reference the initial render
  const initialRender = useRef(true);
  useEffect(() => {
    // Only advance if this isn't the first render and the data is not loading
    if (!initialRender.current && !loading) {
      // set a timeout so we don't refresh the ad a crazy amount of times
      const timer = setTimeout(() => {
        window?.googletag?.cmd?.push?.(() => {
          // refresh both ads on the page
          window.googletag.pubads?.().refresh();
        });
      }, 1000);

      return () => clearTimeout(timer);
    } else if (!loading) {
      // Loading will trigger this again after initial render so
      // once initial render is over and its not loading,
      // set the initialRender to false.
      initialRender.current = false;
    }
  }, [loading]);

  useEffect(() => {
    getStrainPhotos(strain.slug, currentPage).then((response) => {
      const { data, metadata } = response;

      setLoading(false);
      setPhotos(
        currentPage === 1
          ? [...initialPhotos, ...data.slice(initialPhotos.length, data.length)]
          : data
      );
      setTotalPhotos(metadata.totalCount);
    });
  }, [currentPage]);

  return (
    <Fragment>
      <MetaTags
        canonicalParams={[]}
        description={`See photos of ${strain.name} cannabis buds. Browse user-submitted photos of ${strain.name} weed and upload your own images of this marijuana strain.`}
        image={ogImageUrl}
        title={`Photos of ${strain.name} Weed Strain Buds | Leafly`}
      />

      <div className="container pt-xxl">
        <h1 className="mb-sm">{`${strain.name} photos`}</h1>
        {photos.length !== 0 && (
          <p>
            People shared these photos of the cannabis strain {strain.name}.
          </p>
        )}
        <div className="gam-rectangular-fixed-height my-xxl">
          <GoogleAd
            adPath={ads.strainPhotosUpper.path}
            targeting={{ strain: strain.slug }}
            sizes={{ desktop: [728, 90], mobile: [320, 50] }}
          />
        </div>
        {loading ? (
          <div className="flex flex-wrap justify-between">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="col md:col-1/2 lg:col-1/3 mb-md overflow-hidden"
              >
                <div className="image--1:1 relative">
                  <Placeholder width="100%" height="100%" className="absolute">
                    <Rect width="100%" height="100%" />
                  </Placeholder>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <PhotoGallery
              images={photos}
              emptyStateMessage="No photos of this strain yet."
              eagerLoadWhen={(_image: string, i: number) =>
                i < EAGER_LOAD_COUNT
              }
            />
            {photos.length === 0 && (
              <Button
                href={`/strains/${strain.slug}`}
                className="mb-xxl mx-auto"
              >
                back to {strain.name} strain
              </Button>
            )}
          </>
        )}
        {photos.length !== 0 && (
          <div className="gam-square-fixed-height mt-xl">
            <GoogleAd
              adPath={ads.strainPhotosLower.path}
              targeting={{ strain: strain.slug }}
              sizes={{ desktop: [300, 250], mobile: [300, 250] }}
            />
          </div>
        )}
        <PageNavigation
          currentPage={currentPage}
          totalPages={Math.ceil(totalPhotos / 36)}
          className="mt-6"
        />
      </div>

      <Breadcrumbs>
        <Breadcrumb href="/strains"> Weed Strains </Breadcrumb>
        <Breadcrumb
          href={
            strain.phenotype
              ? `/strains/lists/category/${strain.phenotype.toLowerCase()}`
              : `/strains/${strain.slug}`
          }
        >
          {strain.phenotype ? strain.phenotype : strain.name}
        </Breadcrumb>
        <Breadcrumb href={`/strains/${strain.slug}`}>{strain.name}</Breadcrumb>
        <Breadcrumb currentLocation href={`/strains/${strain.slug}/photos`}>
          {strain.name} Photos
        </Breadcrumb>
      </Breadcrumbs>
    </Fragment>
  );
};
export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context;
  const slug = Array.isArray(query?.strainSlug)
    ? query.strainSlug[0]
    : query.strainSlug || "";

  const redirect = getStrainRedirect(slug, `/strains/${slug}/photos`);

  if (redirect) {
    return {
      redirect,
    };
  }

  const strain = await getStrain(slug);

  if (!strain) {
    return {
      notFound: true,
    };
  }

  return { props: { initialPhotos: strain.highlightedPhotos, strain } };
};

export default Photos;
